import { useMemo } from 'react';

import { usePageLayout } from '@libs/common-next';
import type { Inventory } from '@libs/common-node';

import { useQueryInventoryPairs } from '@/entities/inventory';
import { type ProductVariant, useProduct } from '@/entities/product';
import { useQueryRecommendedProducts } from '@/entities/recommended-products';

import { mapFBTProducts, mapStockDataToAlgoliaProducts } from '../lib';

export function useRecommendedProducts({ storeId, product }: { storeId: string; product: ProductVariant }) {
    const { user } = usePageLayout();
    const customerId = String(user?.customerprofileid ?? '0');

    const { tailored } = useProduct();
    const selectedProductStock = tailored?.variants[product?.sku ?? ''].BOPIS?.availableStock ?? 0;

    const { data: recommendedProducts = [] } = useQueryRecommendedProducts({
        productId: product.sku,
        customerId,
        storeId,
    });

    const storesku = recommendedProducts.map(product => ({
        sku: String(product.id),
        store: storeId,
    }));
    const { data: inventories } = useQueryInventoryPairs({
        storesku,
        options: {
            enabled: storesku.length > 0,
        },
    });

    const recommendedProductsWithInventory = useMemo(
        () => mapStockDataToAlgoliaProducts(recommendedProducts, inventories as Inventory[]),
        [recommendedProducts, inventories]
    );

    const mappedFBTProducts = useMemo(
        () =>
            mapFBTProducts({
                product,
                productStock: selectedProductStock,
                recommendedProducts: recommendedProductsWithInventory,
            }),
        [product, selectedProductStock, recommendedProductsWithInventory]
    );

    return mappedFBTProducts;
}
